import React, { useCallback } from 'react'

import { Button } from 'antd'

import { Icon, T, TextArea } from 'src/govwell-ui'
import { KeyboardKey } from 'src/types'

type Props = {
  value: string
  onValueChange: (value: string) => void
  onSend: () => void
  isDisabled: boolean
  suggestedPrompts?: string[]
  onSelectPrompt?: (prompt: string) => void
  showPrompts?: boolean
}

const StaffAIChatInputArea = ({
  value,
  onValueChange,
  onSend,
  isDisabled,
  suggestedPrompts = [],
  onSelectPrompt,
  showPrompts = false,
}: Props) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === KeyboardKey.Enter && !e.shiftKey) {
        e.preventDefault()
        onSend()
      }
    },
    [onSend]
  )

  const isSendDisabled = isDisabled || !value.trim()

  return (
    <div className="shrink-0 border-t border-neutral-200 bg-white py-4">
      <div className="mx-auto w-full max-w-4xl px-6">
        {/* Input Box */}
        <div className="mb-3 flex items-end gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-sm [&_textarea]:!border-0 [&_textarea]:focus-within:![box-shadow:none]">
          <TextArea
            ariaLabel="Message to send to AI Assistant"
            placeholder="Ask anything..."
            width="100%"
            value={value}
            minRows={1}
            maxRows={4}
            onValueChange={onValueChange}
            onKeyDown={handleKeyDown}
            isDisabled={isDisabled}
          />
          <Button
            type="primary"
            onClick={onSend}
            disabled={isSendDisabled}
            aria-label="Send message"
            icon={<Icon.FaPaperPlane className="h-4 w-4" />}
          />
        </div>

        {/* Suggested Prompts */}
        {showPrompts && suggestedPrompts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((prompt) => (
              <Button key={prompt} size="small" onClick={() => onSelectPrompt?.(prompt)}>
                {prompt}
              </Button>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <T.BodySmall className="mt-3 text-center text-neutral-400">
          GovWell AI can make mistakes. Check important info.
        </T.BodySmall>
      </div>
    </div>
  )
}

export default React.memo(StaffAIChatInputArea)
